<script setup lang="ts">
// Imports
import { PageEnum } from "@/enums";
import * as sentry from "@sentry/vue";

// Components
import getQuoteComponent from "@/components/form/getQuote/GetQuote.vue";
import selectCoverComponent from "@/components/form/selectCover/SelectCover.vue";
import coverSummaryComponent from "@/components/form/coverSummary/CoverSummary.vue";
import confirmationPageComponent from "@/components/form/confirmationPage/ConfirmationPage.vue";
import yourDetailsComponent from "@/components/form/yourDetails/YourDetails.vue";
import paymentDetailsComponent from "@/components/form/paymentDetails/PaymentDetails.vue";
import yourPlanComponent from "@/components/form/yourPlan/YourPlan.vue";
import errorPageComponent from "@/views/ErrorPage.vue";

// Stores
import {
    useBankingStore,
    useCustomerStore,
    useDataLayerStore,
    useGlobalStore,
    useJourneyStore,
    useQuoteStore,
} from "@/stores";
import axios, { type AxiosResponse } from "axios";
import {
    AdobeEventTypeEnum,
    type CustomerResponseModel,
    EndPageEnum,
    EventTypeEnum,
    type PremiumRequestModel,
    type PremiumResponseModel,
    type QuotesRequestModel,
    type QuotesResponseModel,
    type SubmitApplicationRequest,
} from "@/models";
import { onMounted } from "vue";
import { nextTick } from "process";

// Stores
const customer = useCustomerStore();
const dataLayer = useDataLayerStore();
const global = useGlobalStore();
const journey = useJourneyStore();
const quote = useQuoteStore();
const banking = useBankingStore();

const createCustomerHandler = async (): Promise<void> => {
    global.startTick();
    const { data } = await axios<CustomerResponseModel>({
        method: "post",
        url: `${global.env.API_URL}/api/v2/customers`,
        data: {
            ...customer.createCustomerRequest(),
        },
        headers: { "Content-Type": "application/json" },
    });
    global.resetTick();
    if (data.customerID && data.customerSecret) {
        customer.customerID = data.customerID;
        customer.customerSecret = data.customerSecret;
        customer.caciData = data.caciData?.caciResponse;
    } else {
        global.hideForm(EndPageEnum.ErrorPage);
    }
};

const createQuoteAndPremiumHandler = async (): Promise<void> => {
    const { data: createQuote } = await axios<QuotesResponseModel>({
        method: "post",
        url: `${global.env.API_URL}/api/v2/quotes/overfifties`,
        data: {
            tracking: false,
            customerID: customer.customerID,
            dayPhone: customer.dayPhone,
            affiliateID: journey.affiliateID,
            agency: journey.agencyNumber.toString(),
            campaign: journey.campaign,
            monthlyCost: quote.myPPM,
            cashSumOnDeath: quote.mySA,
            staffID: journey.staffId || undefined,
        } as QuotesRequestModel,
        headers: { "Content-Type": "application/json" },
    });

    quote.quoteID = createQuote.quoteID;
    quote.quoteSecret = createQuote.quoteSecret;

    const { data: createPremium } = await axios<PremiumResponseModel>({
        method: "post",
        url: `${global.env.API_URL}/api/v2/premium/overfifties`,
        data: {
            tracking: false,
            quoteID: quote.quoteID,
            ppmamount: quote.myPPM,
            podamount: quote.mySA,
            agency: journey.agencyNumber.toString(),
            campaign: journey.campaign,
            dayPhone: customer.dayPhone,
        } as PremiumRequestModel,
        headers: { "Content-Type": "application/json" },
    });

    quote.premiumID = createPremium.premiumID;
    quote.premiumSecret = createPremium.premiumSecret;

    if (!journey.isClickThrough) {
        await createNonConverter(quote.quoteID);
    }
};

const createNonConverter = async (quoteID: string | number): Promise<void> => {
    await axios<AxiosResponse>({
        method: "post",
        url: `${global.env.API_URL}/api/v2/nonconverter/overfifties/${quoteID}`,
        headers: { "Content-Type": "application/json" },
    });
};

const updateNonConverter = async (quoteID: string | number) => {
    await axios<AxiosResponse>({
        method: "put",
        url: `${global.env.API_URL}/api/v2/nonconverter/overfifties/${quoteID}`,
        headers: { "Content-Type": "application/json" },
        data: {
            quotenumber: quoteID,
            emailssent: 4,
        },
    });
};

const updatePremiumHandler = async (): Promise<void> => {
    await axios<PremiumResponseModel>({
        method: "put",
        url: `${global.env.API_URL}/api/v2/premium/overfifties/${quote.premiumID}`,
        data: {
            tracking: false,
            quoteID: quote.quoteID,
            ppmamount: quote.myPPM,
            podamount: quote.mySA,
            agency: journey.agencyNumber.toString(),
            campaign: journey.campaign,
            dayPhone: customer.dayPhone,
        } as PremiumRequestModel,
        headers: {
            "Content-Type": "application/json",
            "X-API-Key": quote.premiumSecret,
        },
    });
};

const updateCustomerHandler = async (): Promise<void> => {
    await axios<CustomerResponseModel>({
        method: "put",
        url: `${global.env.API_URL}/api/v2/customers/${customer.customerID}`,
        data: {
            quoteNumber: quote.quoteID,
            quoteID: quote.quoteID,
            ...customer.updateCustomerRequest(),
        },
        headers: {
            "Content-Type": "application/json",
            "X-API-Key": customer.customerSecret,
        },
    });
};

const submitApplicationHandler = async (): Promise<void> => {
    await axios({
        method: "post",
        url: `${global.env.API_URL}/api/v2/request/overfifties/${quote.quoteID}`,
        data: {
            tracking: false,
            bankDetails: banking.bankDetailsRequestPart(),
            docs: customer.documentsOption,
        } as SubmitApplicationRequest,
        headers: {
            "Content-Type": "application/json",
        },
    });

    global.contactComplete = true;
    dataLayer.satelliteTrack("contact modal submit");
    global.hideForm(EndPageEnum.ConfirmationPage);
};

const submitSection = async (currentPage: PageEnum, nextPage: PageEnum) => {
    try {
        switch (currentPage) {
            case PageEnum.GetQuote:
                journey.checkAgencyNumber();
                await createCustomerHandler();
                await createQuoteAndPremiumHandler();
                break;
            case PageEnum.SelectCover:
                await updatePremiumHandler();
                break;
            case PageEnum.CoverSummary:
                // Does not send a request to the api
                break;
            case PageEnum.YourDetails:
                await updateCustomerHandler();
                break;
            case PageEnum.PaymentDetails:
                // Does not send a request to the api
                break;
            case PageEnum.YourPlan:
                await submitApplicationHandler();
                await updateNonConverter(quote.quoteID);
                break;
        }

        global.gotoPage(nextPage);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
        sentry.captureException(error);
        dataLayer.pageNumber = 8;
        dataLayer.pageError = {
            errorType: "Page",
            errorCode: error.response?.status,
            errorMessage: error.message,
        };
        dataLayer.errors.push(error.message);
        dataLayer.triggerEvent(EventTypeEnum.ErrorEvent);
        global.resetTick();
        global.hideForm(EndPageEnum.ErrorPage);
    }
};

onMounted(async () => {
    if (journey.isClickThrough) {
        const query = new URLSearchParams(location.search);
        const qKey = query.get("qKey");
        const qValue = query.get("qValue");
        if (qKey && qValue) {
            await quote.preloadFromPremium(qKey, qValue);
        }
        if (quote.myDeal >= 0) {
            global.gotoPage(PageEnum.CoverSummary);
        }
    }
    dataLayer.triggerAdobeEvent(AdobeEventTypeEnum.FormStart);
    nextTick(() => {
        dataLayer.triggerAdobeEvent(AdobeEventTypeEnum.PageView);
    });
});

defineExpose({
    submitSection,
    createCustomerHandler,
    createQuoteAndPremiumHandler,
    updateCustomerHandler,
    submitApplicationHandler,
    createNonConverter,
    updateNonConverter,
});
</script>

<template>
    <div id="content" class="content">
        <template v-if="global.showForm">
            <getQuoteComponent
                v-if="global.step >= 1"
                @submit-section="submitSection"
            />
            <selectCoverComponent
                v-if="global.step >= 2"
                @submit-section="submitSection"
            />
            <coverSummaryComponent
                v-if="global.step >= 3"
                @submit-section="submitSection"
            />
            <yourDetailsComponent
                v-if="global.step >= 4"
                @submit-section="submitSection"
            />
            <paymentDetailsComponent
                v-if="global.step >= 5"
                @submit-section="submitSection"
            />
            <yourPlanComponent
                v-if="global.step >= 6"
                @submit-section="submitSection"
            />
        </template>
        <confirmationPageComponent
            v-if="global.endPage === EndPageEnum.ConfirmationPage"
        />
        <errorPageComponent v-if="global.endPage === EndPageEnum.ErrorPage" />
    </div>
</template>
